import { featureCollection } from "@turf/helpers";
import intersect from "@turf/intersect";
import { buffer } from "@turf/turf";
import union from "@turf/union";
import { LayerInfo } from "src/common/managers/Types";
import { DataCollector } from "src/dataCollector/DataCollector";
import { LayerFunc } from "src/managers/LayerFunc";
import { areaByUserPreference } from "src/managers/PlanarCalculationMethods";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { MapitStateActions, MapitStateActionType } from "src/states/MapitState";

export type MarkIntersect = {
   type: string;
   properties: {
      id: number;
      name: string;
      area: number;
      overlap: number;
      Markblok: unknown;
      Marknr: unknown;
      [key: string]: any | undefined;
   };
   intersect: {
      type: string;
      coordinates: number[][][] | number[][] | number[];
   }
   geometry: {
      type: string;
      coordinates: number[][][] | number[][] | number[];
   };
}

export const SenestMarkerLagÅrstal = 2026;

export class MarkData {
   static async hentMarkerIndenforFeatureCollection<T extends { geometry, properties }>(collection: { features: T[] }, buffer?: number): Promise<T & { marker?: MarkIntersect[] }> {
      const årstal = SenestMarkerLagÅrstal;
      let combined = collection.features[0] as any
      if (collection.features.length > 1) {
         combined = union(collection as any);
      }
      let data = await this.hentMarkerIndenforFeature(combined as any, årstal, buffer);
      data["årstal"] = årstal;
      return data
   }

   static async hentMarkerDyrkede(cvr: string[] | string): Promise<any> {
      if (typeof cvr === "string") {
         cvr = [cvr];
      }
      let marker = await DataCollector.WFS.getFeatureQuery("FVM (Føde, Land og Fisk)", "Marker_" + SenestMarkerLagÅrstal as any,
         'CVR in (' + cvr.join(",") + ')'
      ) as any;
      return marker;
   }

   static async hentMarkerIndenforFeature<T extends { geometry, properties }>(geojson: T, årstal: number, bufferDist?: number): Promise<T & { marker?: MarkIntersect[] }> {
      let sfe = window.structuredClone(geojson);
      let buffered = bufferDist ? buffer(featureCollection([sfe as any]), bufferDist, { units: "meters" }) : featureCollection([sfe as any])
      let resMark = await DataCollector.WFS.getFeatureIntersect("FVM (Føde, Land og Fisk)", "Marker_" + årstal as any, buffered as any);
      // let resMark = await this.getMarkData(sfe.geometry);
      if (resMark) {
         let overlap: any[] = []
         resMark.features.forEach(mark => {
            let intersection = intersect(featureCollection([mark, sfe]))
            if (intersection) {
               mark.properties.overlap = areaByUserPreference(intersection)
               mark.properties.area = areaByUserPreference(mark.geometry);
               mark.intersect = intersection.geometry;
               overlap.push(mark);
            }
         });
         sfe["marker"] = overlap;
      }
      return sfe;
   }

   static async hentMarkerFraSFEListe(liste: { sfe: number }[], årstal: number): Promise<any[]> {
      let x = Promise.all(liste.map(async (a) => {
         return (await PropertyInfoInterface.getGeojsonOfSFE(a.sfe))
      }));
      let y = (await x).map(async (val) => { return await this.hentMarkerIndenforFeature(val.features.length > 1 ? union(val, { ...val.features[0] }) : val.features[0], årstal) });
      let data = await Promise.all(y)
      return data;
   }

   static haFormat(): (nr: number) => { format: string, unit: string } {
      let ha = Intl.NumberFormat("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      let m2 = Intl.NumberFormat("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return (nr: number) => {
         if (nr > 100) {
            return { format: ha.format(nr / 10000) + " ha", unit: "ha" }
         }
         return { format: m2.format(nr) + " m²", unit: "m²" }
      }
   }

   static areaFormater(mark: MarkIntersect) {
      let haFormat = MarkData.haFormat();
      return {
         ...mark,
         properties: {
            ...mark.properties,
            area: haFormat(10000 * mark.properties.IMK_areal).format,
            overlap: haFormat(10000 * mark.properties.IMK_areal * mark.properties.overlap / mark.properties.area).format
         }
      }
   }

   static async markLayerInfo(layerName: string, textByPropertyKeys: string[], markFeatureCollection: any, mapitDispatch: (value: MapitStateActions) => void): Promise<void> {

      let markfc = markFeatureCollection;
      let info: LayerInfo = {
         layerId: -1,
         datasetname: layerName,
         type: LayerFunc.getTypeOfGeoJson(markfc.features),
         readonly: false,
         hasNoDataBehind: true,
         columnMapping: { ["GeoJSON_Features_String"]: 0 },
         styling: {
            textByProperty: textByPropertyKeys
         },
         analysisResult: { noOfRows: markfc.features?.length ?? 0, noOfColumns: 1, headers: [], columnNames: ["coordinates"], columns: [] },
         data: { ["GeoJSON_Features_String"]: markfc },
         geoJson: markfc,
         propertiesInGeoJson: LayerFunc.extractPropertiesFromGeoJSON(markfc),
         propertiesToDisplay: [{
            name: "Marknr",
         }, {
            name: "Markblok",
         }, {
            name: "Afgroede",
            display: { aliasName: "Afgrøde" },
         }, {
            name: "overlap",
            display: { aliasName: "Overlappende areal" },
         }, {
            name: "area",
            display: { aliasName: "Hele markens areal" },
         }, {
            name: "GBanmeldt",
            display: { aliasName: "Anmeldt til støtte" },
         }],
      }
      mapitDispatch({
         type: MapitStateActionType.AddDataLayer,
         payload: { layerInfo: info, zoomToFocus: true },
      })
   }

   static MarkDataSorter<T extends MarkFeature>(a: T, b: T): number {
      let x: string = MarkData.safeString(a.properties.Markblok) ?? "";
      let y: string = MarkData.safeString(b.properties.Markblok) ?? "";
      if (x == y) {
         let xy: string = MarkData.safeString(a.properties.Marknr) ?? "";
         let yy: string = MarkData.safeString(b.properties.Marknr) ?? "";
         return xy?.localeCompare(yy + "") ?? -1;
      }
      return x?.localeCompare(y + "") ?? -1;
   };

   static safeString(str: unknown): string {
      if (typeof str === "string") {
         return str;
      }
      return "";
   }
}

type MarkFeature = {
   properties: {
      Markblok: unknown;
      Marknr: unknown;
   }
}
